import { useState } from 'react';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { useLang } from '../i18n';
import { ConsentCheckboxes } from './ConsentCheckboxes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'eco-input';

const EMPTY = {
  first_name: '', last_name: '', email: '', whatsapp: '', country: '',
  travel_date: '', alternative_date: '', adults: '2', children: '0', children_ages: '',
  preferred_language: 'PT', accommodation: '', pickup_location: '',
  preferred_time: '', flight_number: '', passengers: '2', dropoff_location: '',
  special_requirements: '', notes: '',
};

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="eco-field-label mb-1.5 block">{label}</span>
    {children}
  </label>
);

export const RequestAvailabilityForm = ({
  destination = '',
  product = '',
  experienceId = '',
  experienceSlug = '',
  kind = 'availability',
  meetingPoint = '',
  serviceType = '',
  showAccommodation = false,
  showPickup = false,
  showDropoff = false,
  bare = false,
  testid = 'request-availability-form',
}) => {
  const { t } = useLang();
  const isTransfer = kind === 'transfer';
  const [form, setForm] = useState(EMPTY);
  const [consents, setConsents] = useState({ terms: false, cancellation: false, marketing: false });
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    const travelers = isTransfer
      ? `${form.passengers} ${t('form.passengers').toLowerCase()}`
      : `${form.adults} ${t('form.adults').toLowerCase()}${form.children !== '0' ? ` · ${form.children} ${t('form.children').toLowerCase()}` : ''}`;
    try {
      const res = await fetch(`${API}/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind,
          name: `${form.first_name} ${form.last_name}`.trim(),
          email: form.email,
          whatsapp: form.whatsapp,
          destination,
          product,
          experience: product,
          experience_id: experienceId,
          experience_slug: experienceSlug,
          meeting_point: meetingPoint || form.pickup_location,
          travelers,
          dates: form.travel_date,
          message: form.notes,
          first_name: form.first_name,
          last_name: form.last_name,
          country: form.country,
          travel_date: form.travel_date,
          alternative_date: form.alternative_date,
          adults: form.adults,
          children: form.children,
          children_ages: form.children_ages,
          preferred_language: form.preferred_language,
          accommodation: form.accommodation,
          pickup_location: form.pickup_location,
          dropoff_location: form.dropoff_location,
          preferred_time: form.preferred_time,
          flight_number: form.flight_number,
          passengers: isTransfer ? form.passengers : '',
          service_type: serviceType,
          special_requirements: form.special_requirements,
          consent_terms: consents.terms,
          consent_cancellation: consents.cancellation,
          consent_marketing: consents.marketing,
        }),
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      setReference(data.reference || '');
      setForm(EMPTY);
    } catch {
      setError(t('form.error'));
    } finally {
      setSending(false);
    }
  };

  const ctaKey = kind === 'transfer' ? 'cta.transfer' : (kind === 'proposal' || kind === 'trip') ? 'cta.proposal' : 'cta.availability';

  if (reference) {
    return (
      <div data-testid={`${testid}-success`} className={bare ? '' : 'rounded-2xl border border-eco-green/20 bg-white p-6 shadow-sm'}>
        <span className="grid h-11 w-11 place-items-center rounded-full bg-eco-green/10 text-eco-green"><CheckCircle2 size={22} /></span>
        <h2 className="eco-card-title mt-4">{t('req.received.t')}</h2>
        <p className="eco-number mt-1 text-sm font-semibold text-eco-gold" data-testid={`${testid}-reference`}>{reference}</p>
        <ul className="eco-body mt-4 list-disc space-y-1.5 pl-5">
          <li>{t('req.received.1')}</li>
          <li>{t('req.received.2')}</li>
          <li>{t('req.received.3')}</li>
        </ul>
        <p data-testid={`${testid}-not-confirmed-note`} className="mt-4 rounded-xl bg-eco-gold/10 px-4 py-3 text-xs font-semibold text-eco-ink/70">{t('req.received.note')}</p>
      </div>
    );
  }

  return (
    <form data-testid={testid} onSubmit={submit} className={bare ? '' : 'rounded-2xl border border-eco-ink/10 bg-white p-6 shadow-sm'}>
      <h2 className="text-xl leading-[1.3] font-semibold tracking-[-0.015em] text-eco-ink">{t(ctaKey)}</h2>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <Field label={t('filter.destination')} testid={`${testid}-destination`}>
          <input value={destination} readOnly className={`${inputCls} bg-eco-cream/60`} data-testid={`${testid}-destination-input`} />
        </Field>
        <Field label={t('form.product')} testid={`${testid}-product`}>
          <input value={product} readOnly className={`${inputCls} bg-eco-cream/60`} data-testid={`${testid}-product-input`} />
        </Field>
        {serviceType && (
          <Field label={t('form.servicetype')} testid={`${testid}-servicetype`}>
            <input value={serviceType} readOnly className={`${inputCls} bg-eco-cream/60`} data-testid={`${testid}-servicetype-input`} />
          </Field>
        )}
        <Field label={t('form.travelDate')} testid={`${testid}-travel-date`}>
          <input required type="date" min={new Date().toISOString().split('T')[0]} value={form.travel_date} onChange={(e) => set('travel_date', e.target.value)} className={inputCls} data-testid={`${testid}-travel-date-input`} />
        </Field>
        {isTransfer && (
          <Field label={t('form.time')} testid={`${testid}-preferred-time`}>
            <input type="time" value={form.preferred_time} onChange={(e) => set('preferred_time', e.target.value)} className={inputCls} data-testid={`${testid}-preferred-time-input`} />
          </Field>
        )}
        {!isTransfer && (
          <Field label={t('form.altDate')} testid={`${testid}-alt-date`}>
            <input type="date" min={form.travel_date || new Date().toISOString().split('T')[0]} value={form.alternative_date} onChange={(e) => set('alternative_date', e.target.value)} className={inputCls} data-testid={`${testid}-alt-date-input`} />
          </Field>
        )}
        {isTransfer && (
          <Field label={t('form.flight')} testid={`${testid}-flight`}>
            <input value={form.flight_number} onChange={(e) => set('flight_number', e.target.value)} placeholder="Ex: TP1551" className={inputCls} data-testid={`${testid}-flight-input`} />
          </Field>
        )}
        {isTransfer ? (
          <Field label={t('form.passengers')} testid={`${testid}-passengers`}>
            <select value={form.passengers} onChange={(e) => set('passengers', e.target.value)} className={inputCls} data-testid={`${testid}-passengers-select`}>
              {['1', '2', '3', '4', '5', '6', '7', '8+'].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
        ) : (
          <>
        <Field label={t('form.adults')} testid={`${testid}-adults`}>
          <select value={form.adults} onChange={(e) => set('adults', e.target.value)} className={inputCls} data-testid={`${testid}-adults-select`}>
            {['1', '2', '3', '4', '5', '6', '7', '8+'].map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label={t('form.children')} testid={`${testid}-children`}>
          <select value={form.children} onChange={(e) => set('children', e.target.value)} className={inputCls} data-testid={`${testid}-children-select`}>
            {['0', '1', '2', '3', '4+'].map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
          </>
        )}
        {form.children !== '0' && (
          <Field label={t('form.childrenAges')} testid={`${testid}-children-ages`}>
            <input value={form.children_ages} onChange={(e) => set('children_ages', e.target.value)} placeholder="Ex: 5, 9" className={inputCls} data-testid={`${testid}-children-ages-input`} />
          </Field>
        )}
        <Field label={t('form.language')} testid={`${testid}-language`}>
          <select value={form.preferred_language} onChange={(e) => set('preferred_language', e.target.value)} className={inputCls} data-testid={`${testid}-language-select`}>
            {['PT', 'EN', 'FR', 'DE'].map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        {showAccommodation && (
          <Field label={t('form.accommodation')} testid={`${testid}-accommodation`}>
            <input value={form.accommodation} onChange={(e) => set('accommodation', e.target.value)} className={inputCls} data-testid={`${testid}-accommodation-input`} />
          </Field>
        )}
        {showPickup && (
          <Field label={t('form.pickup')} testid={`${testid}-pickup`}>
            <input value={form.pickup_location} onChange={(e) => set('pickup_location', e.target.value)} className={inputCls} data-testid={`${testid}-pickup-input`} />
          </Field>
        )}
        {showDropoff && (
          <Field label={t('form.dropoff')} testid={`${testid}-dropoff`}>
            <input value={form.dropoff_location} onChange={(e) => set('dropoff_location', e.target.value)} className={inputCls} data-testid={`${testid}-dropoff-input`} />
          </Field>
        )}
        <div className="sm:col-span-2">
          <Field label={t('form.special')} testid={`${testid}-special`}>
            <input value={form.special_requirements} onChange={(e) => set('special_requirements', e.target.value)} className={inputCls} data-testid={`${testid}-special-input`} />
          </Field>
        </div>
        <Field label={t('form.firstname')} testid={`${testid}-firstname`}>
          <input required value={form.first_name} onChange={(e) => set('first_name', e.target.value)} className={inputCls} data-testid={`${testid}-firstname-input`} />
        </Field>
        <Field label={t('form.lastname')} testid={`${testid}-lastname`}>
          <input required value={form.last_name} onChange={(e) => set('last_name', e.target.value)} className={inputCls} data-testid={`${testid}-lastname-input`} />
        </Field>
        <Field label={t('form.email')} testid={`${testid}-email`}>
          <input required type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputCls} data-testid={`${testid}-email-input`} />
        </Field>
        <Field label={t('form.whatsapp')} testid={`${testid}-whatsapp`}>
          <input value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} className={inputCls} data-testid={`${testid}-whatsapp-input`} />
        </Field>
        <Field label={t('form.country')} testid={`${testid}-country`}>
          <input value={form.country} onChange={(e) => set('country', e.target.value)} className={inputCls} data-testid={`${testid}-country-input`} />
        </Field>
        <div className="sm:col-span-2">
          <Field label={t('form.notes')} testid={`${testid}-notes`}>
            <textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} className={inputCls} data-testid={`${testid}-notes-input`} />
          </Field>
        </div>
      </div>
      <div className="mt-5">
        <ConsentCheckboxes testid={`${testid}-consent`} value={consents} onChange={setConsents} />
      </div>
      {error && <p data-testid={`${testid}-error`} className="mt-3 text-sm font-medium text-red-500">{error}</p>}
      {!consents.terms && <p data-testid={`${testid}-terms-hint`} className="mt-3 text-xs text-eco-ink/45">{t('consent.hint')}</p>}
      <button type="submit" data-testid={`${testid}-submit`} disabled={sending || !consents.terms} className="eco-btn-primary group mt-4 w-full disabled:opacity-60">
        {sending ? <Loader2 size={16} className="animate-spin" /> : <>{t(ctaKey)} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" /></>}
      </button>
    </form>
  );
};
